import { API_URL } from "./Config";
import { formatDate } from "./DateUtils";
import { GetComplaintDetailsAsync, IComplaintDetails } from "./IComplaint";
import {
  GetComplianceInvestigationDetailsAsync,
  IComplianceInvestigationDetails,
} from "./IComplianceInvestigation";
import { IDetailsResponse } from "./IDetailsResponse";
import { GetIncidentDetailsAsync, IIncidentDetails } from "./IIncident";
import {
  GetLicenseeReturnDetailsAsync,
  ILicenseeReturnDetails,
} from "./ILicenseeReturns";
import { GetLicenseDetailsAsync, ILicenceDetails } from "./LicenceDetails";
import { PageResult } from "./Pagination";

export interface INonCompliance {
  noncomplianceid: string;
  type: string;
  reference: string;
  subject: string;
  status: string;
  licenceid: string;
  licenceprofileid: string;
  datesubmitted: string;
  condition: string;
  complianceinvestigationid: string;
  licenseesubmissionid: string;
  incidentid: string;
  complaintid: string;
  description: string;
  dateofnoncompliance: string;
  dateofnotification: string;
}

export interface InonCompliancesList extends PageResult {
  list: INonCompliance[];
}

export interface INonComplianceDetails extends IDetailsResponse {
  noncomplianceid: string;
  type: string;
  reference: string;
  subject: string;
  status: string;
  licenceid: string;
  licenceprofileid: string;
  datesubmitted: string;
  condition: string;
  complianceinvestigationid: string;
  licenseesubmissionid: string;
  incidentid: string;
  complaintid: string;
  description: string;
  dateofnoncompliance: string;
  dateofnotification: string;
  complianceInvestigationDetails?: IComplianceInvestigationDetails;
  licenseeReturnDetails?: ILicenseeReturnDetails;
  licenceDetails: ILicenceDetails;
  incidentDetails?: IIncidentDetails;
  complaintDetails?: IComplaintDetails;
}

export const GetNonCompliancesByComplianceInvestigationFetchUrl = (
  complianceInvestigationId: string,
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any
) => {
  var params = `&page=${pageNo}&per_page=${recordsPerPage}`;

  var filterParams = "";
  if (searchText && searchText.length > 0) {
    filterParams += `&search_term=${encodeURIComponent(searchText)}`;
  }

  let url = `${API_URL}/NonCompliance/listbyciid?ci_id=${complianceInvestigationId}${params}${filterParams}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/noncompliances-list-byci`;
  }

  return url;
};

export const GetNonCompliancesByLicenseeReturnFetchUrl = (
  licenseeReturnId: string,
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any
) => {
  var params = `&page=${pageNo}&per_page=${recordsPerPage}`;

  var filterParams = "";
  if (searchText && searchText.length > 0) {
    filterParams += `&search_term=${encodeURIComponent(searchText)}`;
  }

  let url = `${API_URL}/NonCompliance/listbylrid?lr_id=${licenseeReturnId}${params}${filterParams}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/noncompliances-list-byci`;
  }

  return url;
};

export const GetNonCompliancesByIncidentFetchUrl = (
  incidentId: string,
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any
) => {
  var params = `&page=${pageNo}&per_page=${recordsPerPage}`;

  var filterParams = "";
  if (searchText && searchText.length > 0) {
    filterParams += `&search_term=${encodeURIComponent(searchText)}`;
  }

  let url = `${API_URL}/NonCompliance/listbyincidentid?incident_id=${incidentId}${params}${filterParams}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/noncompliances-list-byci`;
  }

  return url;
};

export const GetNonCompliancesByComplaintFetchUrl = (
  complaintId: string,
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any
) => {
  var params = `&page=${pageNo}&per_page=${recordsPerPage}`;

  var filterParams = "";
  if (searchText && searchText.length > 0) {
    filterParams += `&search_term=${encodeURIComponent(searchText)}`;
  }

  let url = `${API_URL}/NonCompliance/listbycomplaintid?complaint_id=${complaintId}${params}${filterParams}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/noncompliances-list-byci`;
  }

  return url;
};

export const GetNonCompliancesBySiteVisitFetchUrl = (
  siteVisitId: string,
  pageNo: number,
  recordsPerPage: number,
  searchText: string,
  filters: any
) => {
  var params = `&page=${pageNo}&per_page=${recordsPerPage}`;

  var filterParams = "";
  if (searchText && searchText.length > 0) {
    filterParams += `&search_term=${encodeURIComponent(searchText)}`;
  }

  let url = `${API_URL}/NonCompliance/listbysvid?sv_id=${siteVisitId}${params}${filterParams}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/noncompliances-list-svci`;
  }

  return url;
};

export async function GetNonComplianceDetailsAsync(
  nonComplianceId: string
): Promise<INonComplianceDetails> {
  var url = `${API_URL}/NonCompliance/byid?nc_id=${nonComplianceId}`;

  if (process.env.REACT_APP_EPA_ENV === "localhost") {
    url = `${API_URL}/noncompliance-details`;
  }

  var response = await fetch(url);
  var body = <INonComplianceDetails>await response.json();

  const licenceDetails = await GetLicenseDetailsAsync(body.licenceid);

  if (body.complianceinvestigationid) {
    body.complianceInvestigationDetails =
      await GetComplianceInvestigationDetailsAsync(
        body.complianceinvestigationid
      );
  }

  if (body.licenseesubmissionid) {
    body.licenseeReturnDetails = await GetLicenseeReturnDetailsAsync(
      body.licenseesubmissionid
    );
  }

  if (body.incidentid) {
    body.incidentDetails = await GetIncidentDetailsAsync(body.incidentid);
  }

  if (body.complaintid) {
    body.complaintDetails = await GetComplaintDetailsAsync(body.complaintid);
  }

  return <INonComplianceDetails>{
    response: response,
    noncomplianceid: body.noncomplianceid,
    type: body.type,
    reference: body.reference,
    subject: body.subject,
    status: body.status,
    licenceid: body.licenceid,
    licenceprofileid: body.licenceprofileid,
    datesubmitted: formatDate(body.datesubmitted),
    condition: body.condition,
    complianceinvestigationid: body.complianceinvestigationid,
    licenseesubmissionid: body.licenseesubmissionid,
    incidentid: body.incidentid,
    complaintid: body.complaintid,
    description: body.description,
    dateofnoncompliance: formatDate(body.dateofnoncompliance),
    dateofnotification: formatDate(body.dateofnotification),
    complianceInvestigationDetails: body.complianceInvestigationDetails,
    licenseeReturnDetails: body.licenseeReturnDetails,
    licenceDetails: licenceDetails,
    incidentDetails: body.incidentDetails,
    complaintDetails: body.complaintDetails,
  };
}
